import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { FiUser, FiMapPin, FiCalendar, FiMonitor, FiLayout, FiShield, FiCode, FiPenTool, FiTrendingUp, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import ScrollGrid from './ScrollGrid';
import FlickerText from './FlickerText';
import './About.css';

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.2 });
  const [activeSlide, setActiveSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const touchStartX = useRef(null);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-advance carousel on mobile
  useEffect(() => {
    if (!isMobile) return;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(timer);
  }, [isMobile, activeSlide]);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        setActiveSlide((prev) => (prev + 1) % 3);
      } else {
        setActiveSlide((prev) => (prev - 1 + 3) % 3);
      }
    }
    touchStartX.current = null;
  }, []);

  const capabilities = [
    {
      number: '01',
      title: 'Scalable Interfaces',
      description: 'Prioritizing responsive layouts and component architecture. I build front-ends that adapt gracefully across all devices.',
      icon: <FiMonitor />,
      color: '#7c6aef',
    },
    {
      number: '02',
      title: 'Expressive Design',
      description: 'Motion isn\'t decoration; it\'s communication. I design interfaces that respond with organic, human-centric movement.',
      icon: <FiLayout />,
      color: '#9b6aef',
    },
    {
      number: '03',
      title: 'User-First Approach',
      description: 'Accessibility and usability are woven into every design decision. Creating seamless experiences for all users.',
      icon: <FiShield />,
      color: '#06b6d4',
    },
  ];

  const exploringTags = ['RESPONSIVE DESIGN', 'REACT ECOSYSTEM', 'UX MOTION'];

  return (
    <section className="about" id="about" ref={ref}>
      <ScrollGrid fadeVariant="fade-center" />
      <div className="container">
        <motion.div
          className="about-label-row"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
          transition={{ duration: 0.5 }}
        >
          <span className="section-label">About Me</span>
        </motion.div>

        <div className="about-hero-grid">
          <motion.div
            className="about-hero-left"
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h2 className="section-big-title">
              <FlickerText text="WHO AM" isInView={isInView} /><br />
              <span className="accent-purple"><FlickerText text="I" isInView={isInView} delay={0.4} /></span><span className="muted">?</span>
            </h2>
            <p className="about-name-line">
              I am <strong>JAY MARC C. MERILO</strong>
            </p>
            <div className="about-role-tags">
              <span className="about-role-tag"><FiCode size={14} /> Front-end Developer</span>
              <span className="about-role-tag"><FiPenTool size={14} /> UI/UX Designer</span>
              <span className="about-role-tag"><FiTrendingUp size={14} /> Aspiring Full-Stack Developer</span>
            </div>
          </motion.div>

          <motion.div
            className="about-hero-right"
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <p className="about-intro">
              I am <strong>Jay Marc C. Merilo</strong>. I am a Web Developer and UI/UX Designer. 
              I don't just write code; I craft interfaces that bridge the gap between complex 
              logic and human emotion. My approach focuses on the most efficient path to a 
              seamless user journey.
            </p>
            
            <div className="about-exploring glass">
              <div className="exploring-header">
                <span className="exploring-status">SYSTEM STATUS</span>
                <h4>Currently Exploring</h4>
              </div>
              <div className="exploring-tags">
                {exploringTags.map((tag, i) => (
                  <span key={i} className="exploring-tag">{tag}</span>
                ))}
              </div>
              <p className="exploring-quote">
                "Tools change, principles remain. I build for the next generation of the web."
              </p>
            </div>
          </motion.div>
        </div>

        {/* Capabilities Cards — Carousel on mobile */}
        <div
          className={`capabilities-section ${isMobile ? 'carousel-mode' : ''}`}
          onTouchStart={isMobile ? handleTouchStart : undefined}
          onTouchEnd={isMobile ? handleTouchEnd : undefined}
        >
          <div
            className="capabilities-grid"
            style={isMobile ? { transform: `translateX(-${activeSlide * 100}%)` } : undefined}
          >
            {capabilities.map((cap, index) => (
              <motion.div
                className="capability-card glass"
                key={index}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.12 }}
                whileHover={!isMobile ? { y: -6, borderColor: `${cap.color}40` } : undefined}
              >
                <div className="cap-number" style={{ color: cap.color }}>{cap.number}</div>
                <h3>{cap.title}</h3>
                <p>{cap.description}</p>
                <div className="cap-icon" style={{ color: cap.color }}>
                  {cap.icon}
                </div>
                <div className="cap-glow" style={{ background: cap.color }} />
              </motion.div>
            ))}
          </div>

          {/* Carousel dots & arrows — mobile only */}
          {isMobile && (
            <div className="carousel-controls">
              <button
                className="carousel-arrow clickable"
                onClick={() => setActiveSlide((prev) => (prev - 1 + 3) % 3)}
                aria-label="Previous"
              >
                <FiChevronLeft size={18} />
              </button>
              <div className="carousel-dots">
                {capabilities.map((_, i) => (
                  <button
                    key={i}
                    className={`carousel-dot clickable ${i === activeSlide ? 'active' : ''}`}
                    onClick={() => setActiveSlide(i)}
                    aria-label={`Slide ${i + 1}`}
                  />
                ))}
              </div>
              <button
                className="carousel-arrow clickable"
                onClick={() => setActiveSlide((prev) => (prev + 1) % 3)}
                aria-label="Next"
              >
                <FiChevronRight size={18} />
              </button>
            </div>
          )}
        </div>

        {/* Info Cards Row */}
        <motion.div
          className="about-info-row"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <div className="about-info-card glass">
            <FiUser className="info-card-icon" />
            <div>
              <span className="info-card-label">Role</span>
              <span className="info-card-value">UI/UX Designer / Developer</span>
            </div>
          </div>
          <div className="about-info-card glass">
            <FiMapPin className="info-card-icon" />
            <div>
              <span className="info-card-label">Location</span>
              <span className="info-card-value">Santa Maria, Bulacan, PH</span>
            </div>
          </div>
          <div className="about-info-card glass">
            <FiCalendar className="info-card-icon" />
            <div>
              <span className="info-card-label">Education</span>
              <span className="info-card-value">BSIT - PUP (2022 - Present)</span>
            </div>
          </div>
        </motion.div>

        {/* Stats Row */}
        <motion.div
          className="about-stats"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 20 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <div className="stat-item">
            <span className="stat-number">5+</span>
            <span className="stat-label">Projects Built</span>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <span className="stat-number">3+</span>
            <span className="stat-label">Years Learning</span>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <span className="stat-number">1</span>
            <span className="stat-label">Internship</span>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <span className="stat-number">4</span>
            <span className="stat-label">Certifications</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
